import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button font-body text-base font-semibold transition-all duration-fast ease-bounce disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.96] active:shadow-clay-pressed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-clay-sm hover:brightness-95 hover:shadow-clay',
        destructive: 'bg-destructive text-destructive-foreground shadow-clay-sm hover:brightness-95',
        outline:
          'border-2 border-brand-navy bg-transparent text-foreground hover:bg-brand-navy/5',
        secondary: 'bg-secondary text-secondary-foreground shadow-clay-sm hover:bg-secondary/80',
        ghost: 'hover:bg-secondary hover:text-secondary-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        gradient: 'accent-gradient text-primary-foreground shadow-clay hover:brightness-95 hover:shadow-clay-lg',
      },
      size: {
        // default matches the 44px control height (Input/.clay-select) so a
        // button aligns with fields in the same row. lg stays tall for the
        // marketing/landing CTAs.
        default: 'h-11 px-5',
        sm: 'h-10 rounded-md px-4 text-sm',
        lg: 'h-14 px-8',
        icon: 'h-11 w-11 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
